import React, { useContext, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ShopContext } from '../context/shopcontext';
import { MenuIcon, UserIcon, XIcon } from '@heroicons/react/outline';
import { useLocation } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  Gem,
  InboxIcon,
  LucideHandHeart,
  Mailbox,
  MapPin,
  Shield,
  ShieldQuestionIcon,
} from 'lucide-react';
import { BiDonateHeart } from 'react-icons/bi';
import { MdArtTrack, MdPolicy } from 'react-icons/md';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import CouponDisplay from './CouponDisplay';

const Navbar = () => {
  const [visible, setVisible] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const { navigate, token, setToken, setCartItems, setWishlist } = useContext(ShopContext);
  const location = useLocation();

  const logout = () => {
    setToken(null);
    localStorage.removeItem('token');
    setCartItems({});
    setWishlist([]);
    navigate('/login');
  };

  const isHomePage = location.pathname === '/';

  const toggleCategories = () => {
    setCategoriesOpen(!categoriesOpen);
  };

  // Toggle sidebar function
  const toggleSidebar = () => {
    setVisible(!visible);
  };

  const categories = [
    {
      name: 'Necklace',
      slug: 'Necklace',
    },
    {
      name: 'Bangles',
      slug: 'Bangles',
    },
    {
      name: 'Harams',
      slug: 'Harams',
    },
    {
      name: 'Earrings',
      slug: 'Earrings',
    },
    {
      name: "Zarviya Specials",
      slug: "Zarviya Specials",
    }
  ];

  return (
    <div className="relative z-[999]">
      {/* Coupon Display - At the very top */}
      <div className="fixed top-0 left-0 w-full z-[101]">
        <CouponDisplay />
      </div>

      {/* Navbar - Positioned below CouponDisplay with top margin */}
      <div
        className={`fixed left-0  w-full h-16 flex items-center justify-between px-4 sm:px-6 font-medium mx-auto z-[100] transition-all duration-300`}
        style={{
          top: 'var(--coupon-height, 40px)', // Adjust this value based on your CouponDisplay height
          background: '#2a081a',
          backdropFilter: 'blur(10px)',
          boxShadow: 'inset 0 2px 8px rgba(255,255,255,0.1), inset 0 -2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {/* Left Section: Menu Icon */}
        <div className="flex items-center">
          <MenuIcon
            onClick={toggleSidebar}
            className="w-6 h-6 cursor-pointer text-[#e0c3b5] hover:text-[#fefaf5] transition-all"
          />
        </div>

        {/* Center Section: Logo */}
        <div className="flex-1 flex justify-center h-16 relative overflow-hidden">
          <Link
            to="/"
            className="pointer-events-auto absolute top-0 left-1/2 -translate-x-1/2 h-10 flex items-center"
          >
            <img
              src="https://res.cloudinary.com/dubbklkla/image/upload/v1750021931/zarviya_symbol_loz51y.png"
              alt="zarviya logo"
              className="h-[80px] w-auto drop-shadow-md pointer-events-none"
            />
          </Link>
        </div>

        {/* Right Section: Search and User */}
        <div className="flex items-center gap-4">
          <div className="relative group">
            <UserIcon
              onClick={() => (!token ? navigate('/login') : null)}
              className="w-6 h-6 cursor-pointer text-[#e0c3b5] hover:text-[#fefaf5] transition-all"
              aria-label={token ? 'User Options' : 'Login'}
            />
            {token && (
              <div className="absolute right-0 mt-2 hidden group-hover:block z-50">
                <div className="flex flex-col w-28 py-3 px-4 bg-[#2d161e] border border-[#e0c3b5]/30 shadow-md text-[#e0c3b5] rounded">
                  <p
                    onClick={logout}
                    className="hover:text-[#fefaf5] cursor-pointer transition-colors"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && logout()}
                  >
                    Logout
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar - Reduced width from w-64 to w-52 */}
      <div
        className={`fixed top-16 left-0 h-screen w-52 z-999 shadow-lg flex flex-col transition-transform duration-300 ${
          visible ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          background: '#2a081a',
          backdropFilter: 'blur(10px)',
          boxShadow: 'inset 0 2px 8px rgba(255,255,255,0.1), inset 0 -2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {/* Logo section - Reduced height */}
        <div className="h-34 w-full flex items-center justify-center border-b border-[#2d161e]/30">
          <img
            src="https://res.cloudinary.com/dubbklkla/image/upload/v1750093434/Group_2_1_n1b7ej.svg"
            alt="zarviya logo"
            className="h-32 w-32 items-center ml-4 drop-shadow-md object-contain"
          />
        </div>

        <button
          onClick={() => setVisible(false)}
          className="absolute top-10 right-1 w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#2d161e]/10 transition-all"
        >
          <XIcon className="w-5 h-5 text-[#e0d3c2]" />
        </button>

        <div className="flex-1 bg-[#fefaf5] overflow-y-auto pb-28">
          {/* SHOP - Reduced padding */}
          <div className="border-b border-[#2d161e]/30">
            <div
              className="px-4 py-3 flex items-center justify-between cursor-pointer"
              onClick={toggleCategories}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleCategories()}
            >
              <div className="flex items-center">
                <Gem className="w-4 h-4 mr-2 text-[#2d161e]" />
                <span
                  className={`text-sm ${
                    location.pathname.includes('/collection')
                      ? 'text-[#2d161e] font-medium'
                      : 'text-[#2d161e]'
                  }`}
                >
                  Shop
                </span>
              </div>
              <i
                className={`fas ${categoriesOpen ? 'fa-minus' : 'fa-plus'} text-xs text-[#2d161e]`}
              ></i>
            </div>

            <div
              className={`transition-all duration-300 overflow-hidden z-[100] ${
                categoriesOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {categories.map((cat) => {
                const categoryParam = new URLSearchParams(location.search).get('category');
                const isCategoryActive =
                  location.pathname.includes('/collection') && categoryParam === cat.slug;

                return (
                  <div key={cat.slug} className="mb-2 mx-2">
                    <NavLink
                      to={`/collection?category=${encodeURIComponent(cat.slug)}`}
                      onClick={() => setVisible(false)}
                      className={`block text-xs p-2 rounded-md transition-all shadow-sm text-left ${
                        isCategoryActive
                          ? '!bg-[#2d161e] !text-[#fefaf5] !border-2 !border-[#2d161e] font-semibold'
                          : '!bg-[#fefaf5] !text-[#2d161e] !border !border-[#2d161e]/30 hover:!border-[#2d161e]'
                      }`}
                    >
                      {cat.name}
                    </NavLink>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ABOUT - Reduced padding and font size */}
          <NavLink
            to="/about"
            onClick={() => setVisible(false)}
            className={({ isActive }) =>
              `px-4 py-3 flex items-center justify-between border-b border-[#2d161e]/30 ${
                isActive ? 'text-[#2d161e] font-medium' : 'text-[#2d161e]'
              }`
            }
          >
            <div className="flex items-center">
              <LucideHandHeart className="w-4 h-4 mr-2 text-[#2d161e]" />
              <span className="text-sm">About Us</span>
            </div>
          </NavLink>

          {/* TRACK - Reduced padding and font size */}
          <NavLink
            to="/trackyourOrder"
            onClick={() => setVisible(false)}
            className={({ isActive }) =>
              `px-4 py-3 flex items-center justify-between border-b border-[#2d161e]/30 ${
                isActive ? 'text-[#2d161e] font-medium' : 'text-[#2d161e]'
              }`
            }
          >
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-[#2d161e]" />
              <span className="text-sm">Track Order</span>
            </div>
          </NavLink>

          {/* POLICY - Reduced padding and font size */}
          <NavLink
            to="/policy"
            onClick={() => setVisible(false)}
            className={({ isActive }) =>
              `px-4 py-3 flex items-center justify-between border-b border-[#2d161e]/30 ${
                isActive ? 'text-[#2d161e] font-medium' : 'text-[#2d161e]'
              }`
            }
          >
            <div className="flex items-center">
              <Shield className="w-4 h-4 mr-2 text-[#2d161e]" />
              <span className="text-sm">Our Policy</span>
            </div>
          </NavLink>

          {/* FAQ - Reduced padding and font size */}
          <NavLink
            to="/faq"
            onClick={() => setVisible(false)}
            className={({ isActive }) =>
              `px-4 py-3 flex items-center justify-between border-b border-[#2d161e]/30 ${
                isActive ? 'text-[#2d161e] font-medium' : 'text-[#2d161e]'
              }`
            }
          >
            <div className="flex items-center">
              <AiOutlineQuestionCircle className="w-4 h-4 mr-2 text-[#2d161e]" />
              <span className="text-sm">FAQ</span>
            </div>
          </NavLink>

          {/* INSTAGRAM - Reduced padding and font size */}
          <a
            href="https://www.instagram.com/zarviyajewels"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setVisible(false)}
            className="px-4 py-3 flex items-center justify-between border-b border-[#2d161e]/30 text-[#2d161e] hover:text-[#b09287]"
          >
            <div className="flex items-center">
              <i className="fab fa-instagram w-4 h-4 mr-2 text-[#2d161e]"></i>
              <span className="text-sm">Instagram</span>
            </div>
            <i className="fas fa-external-link-alt text-xs"></i>
          </a>

          {/* FACEBOOK */}
          <a
            href="https://www.facebook.com/share/1CSAGurD6Y/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setVisible(false)}
            className="px-4 py-3 flex items-center justify-between border-b border-[#2d161e]/30 text-[#2d161e] hover:text-[#b09287]"
          >
            <div className="flex items-center">
              <i className="fab fa-facebook w-4 h-4 mr-2 text-[#2d161e]"></i>
              <span className="text-sm">Facebook</span>
            </div>
            <i className="fas fa-external-link-alt text-xs"></i>
          </a>

          {/* YOUTUBE */}
          <a
            href="https://youtube.com/@zarviya.jewels?si=2726oobe6v_7Yfof"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setVisible(false)}
            className="px-4 py-3 flex items-center justify-between border-b border-[#2d161e]/30 text-[#2d161e] hover:text-[#b09287]"
          >
            <div className="flex items-center">
              <i className="fab fa-youtube w-4 h-4 mr-2 text-[#2d161e]"></i>
              <span className="text-sm">YouTube</span>
            </div>
            <i className="fas fa-external-link-alt text-xs"></i>
          </a>

          {/* Account Section - Reduced padding */}
          <div className="mt-auto px-4 py-3 border-t border-[#2d161e]/30">
            {token ? (
              <button
                onClick={() => {
                  logout();
                  setVisible(false);
                }}
                className="w-full py-2 px-3 rounded-md border border-[#2d161e] text-[#2d161e] hover:bg-[#2d161e]/10 hover:text-[#2d161e] transition-all flex items-center justify-center gap-2 text-xs"
              >
                <i className="fas fa-sign-out-alt"></i>
                <span>Logout</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  navigate('/login');
                  setVisible(false);
                }}
                className={`w-full py-2 px-3 rounded-md text-xs ${
                  location.pathname === '/login'
                    ? 'bg-[#2d161e] text-[#fefaf5]'
                    : 'border border-[#2d161e] text-[#2d161e] hover:bg-[#2d161e]/10 hover:text-[#2d161e]'
                } transition-all flex items-center justify-center gap-2`}
              >
                <i className="fas fa-sign-in-alt"></i>
                <span>Login</span>
              </button>
            )}
          </div>

          {/* Footer - Reduced font size */}
          <div className="px-4 py-3 text-center text-[10px] text-[#2d161e]/80 border-t border-[#2d161e]/30">
            © {new Date().getFullYear()} zarviya. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
